import type {LockReason} from '@github-ui/server-persisted-queries/types/IssueLock'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {FormControl, Link, Radio, RadioGroup} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {type ChangeEvent, useCallback, useState} from 'react'

import {BUTTON_LABELS} from '../../constants/buttons'
import {LABELS} from '../../constants/labels'
import {useLockConversation, useUnlockConversation} from '../../mutations/use-conversation-lock'
import styles from './IssueConversationLock.module.css'

type IssueConversationLockDialogProps = {
  issueId: string
  issueNumber: number
  nameWithOwner: string
  isUnlocked?: boolean
  onClose: () => void
}

export const IssueConversationLock = (props: IssueConversationLockDialogProps) => {
  const {isUnlocked = true, issueId, issueNumber, nameWithOwner, onClose} = props
  const {addToast} = useToastContext()
  const [lockedReason, setLockedReason] = useState<LockReason | null>(null)

  const handleClose = useCallback(() => {
    onClose()
    setLockedReason(null)
  }, [onClose])

  const handleMutationError = useCallback(
    (error: Error) => {
      reportError(formatError(error.message))
      // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
      addToast({
        type: 'error',
        message: error.message,
      })
    },
    [addToast],
  )

  const lockConversation = useLockConversation({
    issueId,
    issueNumber,
    nameWithOwner,
    onError: handleMutationError,
  })
  const unlockConversation = useUnlockConversation({
    issueId,
    issueNumber,
    nameWithOwner,
    onError: handleMutationError,
  })

  const handleLockConversation = useCallback(() => {
    if (isUnlocked) {
      handleClose()
      lockConversation(lockedReason || null)
    }
  }, [handleClose, isUnlocked, lockConversation, lockedReason])

  const handleUnlockConversation = useCallback(() => {
    if (!isUnlocked) {
      handleClose()
      unlockConversation()
    }
  }, [handleClose, isUnlocked, unlockConversation])

  const onChangeHandler = useCallback((selected: string | null, e: ChangeEvent<HTMLInputElement> | undefined) => {
    if (e) {
      setLockedReason(e.target.value as LockReason)
      return
    }
  }, [])

  return (
    <Dialog
      title={isUnlocked ? LABELS.lock.title : LABELS.lock.titleUnlock}
      renderBody={
        isUnlocked ? () => LockConversationContent({handleReasonChange: onChangeHandler}) : UnlockConversationContent
      }
      onClose={handleClose}
      footerButtons={[
        {content: BUTTON_LABELS.cancel, onClick: handleClose},
        {
          content: isUnlocked ? LABELS.lock.buttonConfirmLock : LABELS.lock.buttonConfirmUnlock,
          buttonType: 'primary',
          onClick: isUnlocked ? handleLockConversation : handleUnlockConversation,
        },
      ]}
    />
  )
}

const LockConversationContent = ({
  handleReasonChange,
}: {
  handleReasonChange: (selected: string | null, e: ChangeEvent<HTMLInputElement> | undefined) => void
}) => {
  return (
    <div className={styles.dialogContent}>
      <span>
        Other users can&apos;t add new comments to this issue. You and other members of teams with&nbsp;
        <Link
          inline
          href="https://docs.github.com/articles/what-are-the-different-access-permissions"
          target="_blank"
          rel="noreferrer"
        >
          write access
        </Link>
        &nbsp;to this repository can still leave comments that others can see. You can always unlock this issue again in
        the future.
      </span>
      <RadioGroup
        name="choiceReason"
        onChange={(selected, e: ChangeEvent<HTMLInputElement> | undefined) => handleReasonChange(selected, e)}
        className={styles.RadioGroup}
      >
        <RadioGroup.Label>Reason</RadioGroup.Label>
        <FormControl>
          <Radio value={''} />
          <FormControl.Label> No reason</FormControl.Label>
        </FormControl>
        <FormControl>
          <Radio value={'SPAM'} />
          <FormControl.Label>Spam</FormControl.Label>
        </FormControl>
        <FormControl>
          <Radio value={'OFF_TOPIC'} />
          <FormControl.Label>Off-topic</FormControl.Label>
        </FormControl>
        <FormControl>
          <Radio value={'TOO_HEATED'} />
          <FormControl.Label>Too heated</FormControl.Label>
        </FormControl>
        <FormControl>
          <Radio value={'RESOLVED'} />
          <FormControl.Label>Resolved</FormControl.Label>
        </FormControl>
      </RadioGroup>
    </div>
  )
}

const UnlockConversationContent = () => {
  return (
    <div className={styles.dialogContent}>
      Everyone will be able to comment on this issue once more. You can always lock this issue again in the future.
    </div>
  )
}

function formatError(message: string) {
  return new Error(`Issue lock conversation mutation failed with error:${message}`)
}
